import type { AlertAiInfo } from '../../../api/src/alerts/alerts.types';
import { PUBLISHING_PAGE_SIZES } from '../../../api/src/telegram/publishing.types';
import { validateAccessPolicy, type PermissionOverrides } from '../../../api/src/auth/permissions';
import { validateUser } from '../access-policy';
import type {
  PublishingFilters,
  PublishingFilterDirectory,
  PublishingFilterPreview,
} from '../../../api/src/telegram/publishing-filters';
import type {
  PublishingOverview,
  PublishingPageSize,
  PublishingMessageSettings,
  PublishingBot,
  PublishingBotPermissions,
  PublishingSettings,
  PublishingChannelView,
  PublishingHistory,
  PublishingPreview,
  PublishingIntegrationView,
  PublishingCities,
} from '../../../api/src/telegram/publishing.types';
import type { GrantedRole } from '../../../api/src/auth/access-role';
import type {
  AlertSettings,
  AlertMail,
  AlertDecision,
  AlertPage,
  AlertDetail,
  AlertSyncView,
} from '../../../api/src/alerts/alerts.types';
import type { UserPreferences } from '../../../api/src/auth/user-preferences';
import type { DailyScheduleSettings } from '../../../api/src/common/daily-schedule.types';
import type {
  DonorReportSettingsInput,
  DonorReportSettingsView,
} from '../../../api/src/donors/donor-reports.types';
import type {
  DonorActionView,
  DonorArticleView,
  DonorBulkInput,
  DonorBulkPreview,
  DonorBulkResult,
  DonorCsvOptions,
  DonorCsvSample,
  DonorCsvSampleInput,
  DonorFilters,
  DonorHistoryItem,
  DonorImportJob,
  DonorImportPreview,
  DonorList,
  DonorLookupItem,
} from '../../../api/src/donors/donors.types';
import type { FakesCollectionSettingsView } from '../../../api/src/fakes/fakes-collection.types';
import type {
  FakesReportSettingsInput,
  FakesReportSettingsView,
  FakesReportView,
} from '../../../api/src/fakes/fakes-reports.types';
import type {
  FakeClassification,
  FakeDecision,
  FakeRegistryStatus,
  FakeVerdict,
  RelevanceDecision,
} from '../../../api/src/fakes/fakes.types';
import type { N8nDomainsSettingsView } from '../../../api/src/n8n-domains/n8n-domains.types';
import type {
  RegistryCatalogCard,
  RegistryCatalogPage,
  RegistryCatalogQuery,
  RegistryManagement,
  RegistryManagementState,
} from '../../../api/src/registry/registry-catalog.types';
import type {
  RegistryImportInput,
  RegistryImportPreview,
  RegistryImportResult,
} from '../../../api/src/registry/registry-import.types';
import type {
  ManualResourceInput,
  RegistryPage,
  RegistryQuery,
  RegistryResource,
  RegistrySearch,
} from '../../../api/src/registry/registry.types';
import type {
  SearchCatalogProperty,
  SearchDetail,
  SearchOverview,
  SearchProvider,
} from '../../../api/src/search-metrics/search-metrics.types';
import type {
  ApiTokenDto,
  AuditLogPageDto,
  ActivitySummaryDto,
  ActivityReceiptDto,
  CreateApiTokenDto,
  CreatedApiTokenDto,
  DatabaseStorageStateDto,
} from './generated/client';
import {
  validateSectionVisibility,
  type SectionId,
  type SectionVisibility,
} from './generated/section-catalog';
import type {
  AccessGrant,
  AccessOverview,
  AuthConfig,
  SearchKeywordInput,
  SearchKeywordMutation,
  SearchRegion,
  SearchSnapshotPage,
  ChangeLogEntry,
  FakesKeywordList,
  FakesMatrixPage,
  FakesMatrixSettings,
  FakesResultPage,
  FakesRunPage,
  FakesRunSummary,
  FakesSearchProfile,
  IntegrationStatus,
  MetricSnapshot,
  RankDashboard,
  RankProjectDetail,
  RankRun,
  RankTargetCheck,
  ShortsDraft,
  TelegramChannel,
  TelegramOwnedChannel,
  TelegramRegistryEntry,
  TopvisorCatalog,
  TopvisorCatalogImportResult,
  TopvisorReport,
  User,
  Workspace,
} from './types';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(
  path: string,
  init?: RequestInit,
  transform?: (body: T, response: Response) => T,
): Promise<T> {
  const response = await fetch(`/api${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      ...init?.headers,
    },
  });
  const policyHeader = response.headers.get('x-access-policy');
  if (policyHeader) {
    const value = JSON.parse(policyHeader);
    const access = validateAccessPolicy(value);
    if (
      typeof value.userId !== 'string' ||
      !['viewer', 'editor', 'superadmin'].includes(value.role)
    )
      throw new Error('Повреждённый ответ о правах пользователя');
    window.dispatchEvent(
      new CustomEvent('user-access', {
        detail: { userId: value.userId, role: value.role, access },
      }),
    );
  }
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      message?: string | string[];
    } | null;
    const detail = Array.isArray(body?.message) ? body.message.join(', ') : body?.message;
    throw new ApiError(response.status, detail || `Request failed with status ${response.status}`);
  }
  const body = (await response.json()) as T;
  return transform ? transform(body, response) : body;
}

function json(method: 'POST' | 'PATCH' | 'PUT', body: unknown): RequestInit {
  return { method, body: JSON.stringify(body) };
}

export const api = {
  alertAiInfo: (w: string) => request<AlertAiInfo>(`/workspaces/${w}/alerts/mail/ai`),
  alertSettings: (w: string) => request<AlertSettings>(`/workspaces/${w}/alerts/settings`),
  saveAlertSettings: (w: string, input: AlertSettings) =>
    request<AlertSettings>(`/workspaces/${w}/alerts/settings`, json('POST', input)),
  alerts: (w: string, page: number, status = '') =>
    request<AlertPage>(
      `/workspaces/${w}/alerts?${new URLSearchParams({ page: String(page), ...(status ? { status } : {}) })}`,
    ),
  alert: (w: string, id: string) => request<AlertDetail>(`/workspaces/${w}/alerts/${id}`),
  previewAlert: (w: string, input: AlertMail) =>
    request<AlertDecision & { revision: number; deliveryEnabled: boolean }>(
      `/workspaces/${w}/alerts/mail/preview`,
      json('POST', input),
    ),
  yandexAlertStatus: (w: string) =>
    request<AlertSyncView[]>(`/workspaces/${w}/alerts/yandex/status`),
  syncYandexAlerts: (w: string) =>
    request<{ queued: true }>(`/workspaces/${w}/alerts/yandex/sync`, json('POST', {})),
  activityVisit: (scope: { sectionId: string; workspaceId?: string }) =>
    request<ActivityReceiptDto>('/activity/visit', json('POST', scope)),
  activitySummary: (query: {
    days: number;
    workspaceId?: string;
    excludeSelf: boolean;
    afterUserId?: string;
  }) =>
    request<ActivitySummaryDto>(
      `/admin/activity?${new URLSearchParams(
        Object.entries(query)
          .filter(([, value]) => value !== undefined && value !== '')
          .map(([key, value]) => [key, String(value)]),
      )}`,
    ),
  searchMetrics: (workspaceId: string, query: Record<string, string>) =>
    request<SearchOverview>(
      `/workspaces/${workspaceId}/search-metrics?${new URLSearchParams(query)}`,
    ),
  searchProperties: (workspaceId: string) =>
    request<SearchCatalogProperty[]>(`/workspaces/${workspaceId}/search-metrics/properties`),
  searchDetail: (workspaceId: string, propertyId: string, query: Record<string, string>) =>
    request<SearchDetail>(
      `/workspaces/${workspaceId}/search-metrics/properties/${propertyId}?${new URLSearchParams(query)}`,
    ),
  selectSearchProperty: (workspaceId: string, propertyId: string, selected: boolean) =>
    request<SearchCatalogProperty[]>(
      `/workspaces/${workspaceId}/search-metrics/properties/${propertyId}`,
      json('PATCH', { selected }),
    ),
  connectSearch: (workspaceId: string, provider: SearchProvider) =>
    request<{ url: string }>(
      `/workspaces/${workspaceId}/search-metrics/connections`,
      json('POST', { provider }),
    ),
  refreshSearchConnection: (workspaceId: string, id: string) =>
    request<SearchCatalogProperty[]>(
      `/workspaces/${workspaceId}/search-metrics/connections/${id}/refresh`,
      json('POST', {}),
    ),
  disconnectSearch: (workspaceId: string, id: string) =>
    request(`/workspaces/${workspaceId}/search-metrics/connections/${id}`, { method: 'DELETE' }),
  syncSearchMetrics: (workspaceId: string, query: Record<string, string>) =>
    request<{ runIds: string[] }>(
      `/workspaces/${workspaceId}/search-metrics/sync`,
      json('POST', query),
    ),
  registryCatalog: (w: string, query: RegistryCatalogQuery) =>
    request<RegistryCatalogPage>(
      `/workspaces/${w}/registry/catalog?${new URLSearchParams(
        Object.entries(query)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => [k, String(v)]),
      )}`,
    ),
  registryCatalogCard: (w: string, key: string, page = 1) =>
    request<RegistryCatalogCard>(
      `/workspaces/${w}/registry/catalog/card?${new URLSearchParams({ key, page: String(page) })}`,
    ),
  saveRegistryManagement: (
    w: string,
    key: string,
    management: RegistryManagement,
    expectedRevision: number,
  ) =>
    request<RegistryManagementState>(
      `/workspaces/${w}/registry/catalog/management`,
      json('PATCH', { key, management, expectedRevision }),
    ),
  previewRegistryImport: (w: string, input: RegistryImportInput) =>
    request<RegistryImportPreview>(`/workspaces/${w}/registry/import/preview`, json('POST', input)),
  importRegistryResources: (w: string, input: RegistryImportInput, fingerprint: string) =>
    request<RegistryImportResult>(
      `/workspaces/${w}/registry/import`,
      json('POST', { ...input, fingerprint }),
    ),
  createRegistryResource: (w: string, input: ManualResourceInput) =>
    request<RegistryResource>(`/workspaces/${w}/registry/manual`, json('POST', input)),
  registryResource: (w: string, id: string) =>
    request<RegistryResource>(`/workspaces/${w}/registry/manual/${id}`),
  updateRegistryResource: (
    w: string,
    id: string,
    input: ManualResourceInput & { expectedRevision: number },
  ) => request<RegistryResource>(`/workspaces/${w}/registry/manual/${id}`, json('PATCH', input)),
  archiveRegistryResource: (w: string, id: string, expectedRevision: number) =>
    request<RegistryResource>(
      `/workspaces/${w}/registry/manual/${id}/archive`,
      json('POST', { expectedRevision }),
    ),
  searchRegistry: (workspaceId: string, search: string) =>
    request<RegistrySearch>(
      `/workspaces/${workspaceId}/registry/search?${new URLSearchParams({ search })}`,
    ),
  registry: (workspaceId: string, query: RegistryQuery) =>
    request<RegistryPage>(
      `/workspaces/${workspaceId}/registry?${new URLSearchParams(
        Object.entries(query)
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => [key, String(value)]),
      )}`,
    ),
  donors: (w: string, query: DonorFilters & { page: number }) =>
    request<DonorList>(
      `/workspaces/${w}/donors?${new URLSearchParams(
        Object.entries(query)
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => [key, String(value)]),
      )}`,
    ),
  previewDonorBulk: (w: string, input: DonorBulkInput) =>
    request<DonorBulkPreview>(`/workspaces/${w}/donors/bulk/preview`, json('POST', input)),
  executeDonorBulk: (w: string, id: string) =>
    request<DonorBulkResult>(`/workspaces/${w}/donors/bulk/${id}/execute`, json('POST', {})),
  donorActions: (w: string, id: string) =>
    request<DonorActionView[]>(`/workspaces/${w}/donors/${id}/actions`),
  lookupDonors: (w: string, q: string) =>
    request<DonorLookupItem[]>(`/workspaces/${w}/donors/lookup?${new URLSearchParams({ q })}`),
  donor: (w: string, id: string) => request<DonorArticleView>(`/workspaces/${w}/donors/${id}`),
  sampleDonorImport: (w: string, input: DonorCsvSampleInput) =>
    request<DonorCsvSample>(`/workspaces/${w}/donors/import/sample`, json('POST', input)),
  donorSettings: (w: string) =>
    request<DonorReportSettingsView>(`/workspaces/${w}/donors/settings`),
  saveDonorSettings: (w: string, input: DonorReportSettingsInput) =>
    request<DonorReportSettingsView>(`/workspaces/${w}/donors/settings`, json('POST', input)),
  testDonorTelegram: (w: string, revision: number) =>
    request<DonorReportSettingsView>(
      `/workspaces/${w}/donors/settings/test`,
      json('POST', { revision }),
    ),
  donorHistory: (w: string, id: string) =>
    request<DonorHistoryItem[]>(`/workspaces/${w}/donors/${id}/history`),
  saveDonor: (
    w: string,
    payload: {
      url: string;
      note: string;
      links: Array<{ target: string; anchor: string }>;
      revision?: number;
    },
    id?: string,
  ) =>
    request<DonorArticleView>(
      `/workspaces/${w}/donors${id ? `/${id}` : ''}`,
      json(id ? 'PATCH' : 'POST', payload),
    ),
  previewDonorImport: (w: string, csv: string, options?: DonorCsvOptions) =>
    request<DonorImportPreview>(
      `/workspaces/${w}/donors/import/preview`,
      json('POST', { csv, options }),
    ),
  importDonors: (
    w: string,
    csv: string,
    options?: DonorCsvOptions,
    submission?: { requestId: string; fileName: string },
  ) =>
    request<DonorImportJob>(
      `/workspaces/${w}/donors/import`,
      json('POST', { csv, options, ...submission }),
    ),
  donorImports: (w: string) => request<DonorImportJob[]>(`/workspaces/${w}/donors/import/jobs`),
  resumeDonorImport: (w: string, id: string) =>
    request<DonorImportJob>(`/workspaces/${w}/donors/import/jobs/${id}/resume`, json('POST', {})),
  pauseDonors: (w: string, paused: boolean) =>
    request<{ paused: boolean }>(`/workspaces/${w}/donors/pause`, json('POST', { paused })),
  checkDonors: (w: string, id?: string) =>
    request<{ scheduled: boolean }>(
      `/workspaces/${w}/donors${id ? `/${id}` : ''}/recheck`,
      json('POST', {}),
    ),
  archiveDonor: (w: string, id: string) =>
    request<{ archived: boolean }>(`/workspaces/${w}/donors/${id}`, { method: 'DELETE' }),

  apiTokens: () => request<ApiTokenDto[]>('/auth/tokens'),
  createApiToken: (payload: CreateApiTokenDto) =>
    request<CreatedApiTokenDto>('/auth/tokens', json('POST', payload)),
  revokeApiToken: (id: string) => request<ApiTokenDto>(`/auth/tokens/${id}`, { method: 'DELETE' }),
  sections: async () => validateSectionVisibility(await request<unknown>('/sections')),
  updateSection: (id: SectionId, payload: Pick<SectionVisibility, 'visible' | 'revision'>) =>
    request<SectionVisibility>(`/admin/sections/${id}`, json('PATCH', payload)),
  authConfig: () => request<AuthConfig>('/auth/config'),
  me: () =>
    request<{ user: User }>('/auth/me', undefined, (body) => ({ user: validateUser(body.user) })),
  userPreferences: () => request<UserPreferences>('/auth/me/preferences'),
  saveUserPreferences: (payload: Partial<UserPreferences>) =>
    request<UserPreferences>('/auth/me/preferences', json('PATCH', payload)),
  devLogin: () =>
    request<{ user: User }>('/auth/dev', { method: 'POST' }, (body) => ({
      user: validateUser(body.user),
    })),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),
  integrations: () => request<IntegrationStatus>('/system/integrations'),
  access: () => request<AccessOverview>('/admin/access'),
  updateUser: (id: string, payload: { displayName: string; avatarUrl: string | null }) =>
    request<Omit<User, 'isAdmin'>>(`/admin/access/users/${id}`, json('PATCH', payload)),
  adminWorkspaces: () => request<Workspace[]>('/admin/workspaces'),
  databaseStorage: () => request<DatabaseStorageStateDto>('/admin/database-storage'),
  refreshDatabaseStorage: () =>
    request<DatabaseStorageStateDto>('/admin/database-storage/refresh', { method: 'POST' }),
  updateWorkspace: (
    id: string,
    payload: Partial<Pick<Workspace, 'name' | 'siteUrl' | 'ownerId'>>,
  ) => request<Workspace>(`/admin/workspaces/${id}`, json('PATCH', payload)),
  deleteWorkspace: (id: string) =>
    request<{ deleted: true; id: string }>(`/admin/workspaces/${id}`, { method: 'DELETE' }),
  grantAccess: (email: string, role: GrantedRole = 'viewer') =>
    request<AccessGrant>('/admin/access', json('POST', { email, role })),
  updateAccessRole: (id: string, role: GrantedRole, expectedRole: GrantedRole, revision: number) =>
    request<AccessGrant>(
      `/admin/access/${id}/role`,
      json('PATCH', { role, expectedRole, revision }),
    ),
  updatePermissions: (
    id: string,
    payload: { role: GrantedRole; overrides: PermissionOverrides; revision: number },
  ) => request<AccessGrant>(`/admin/access/${id}/permissions`, json('PATCH', payload)),
  revokeAccess: (id: string) =>
    request<{ deleted: true; email: string }>(`/admin/access/${id}`, { method: 'DELETE' }),
  workspacePolicy: () => request<{ personalWorkspacesEnabled: boolean }>('/workspace-policy'),
  shareWorkspace: (id: string) => request<Workspace>(`/workspaces/${id}/share`, json('POST', {})),
  auditLog: (
    filters: {
      actorId?: string;
      workspaceId?: string;
      status?: string;
      cursor?: string;
      limit?: number;
    } = {},
  ) =>
    request<AuditLogPageDto>(
      `/admin/audit-log?${new URLSearchParams(
        Object.entries(filters)
          .filter(([, value]) => value !== undefined && value !== '')
          .map(([key, value]) => [key, String(value)]),
      )}`,
    ),
  changeLog: (id: string) => request<ChangeLogEntry[]>(`/workspaces/${id}/change-log`),
  saveSerpColors: (
    workspaceId: string,
    colors: import('../../../api/src/fakes/serp-appearance').SerpColors,
    revision: number,
  ) =>
    request<Workspace>(
      `/workspaces/${workspaceId}/serp-colors`,
      json('PATCH', { colors, revision }),
    ),
  fakesMapPreferences: (workspaceId: string) =>
    request<import('../../../api/src/fakes/fakes-matrix.types').FakesMapPreferences>(
      `/workspaces/${workspaceId}/fakes/map-preferences`,
    ),
  saveFakesMapPreferences: (
    workspaceId: string,
    input: import('../../../api/src/fakes/fakes-matrix.types').FakesMapPreferences,
  ) =>
    request<import('../../../api/src/fakes/fakes-matrix.types').FakesMapPreferences>(
      `/workspaces/${workspaceId}/fakes/map-preferences`,
      json('PUT', input),
    ),
  fakesGroupedMap: (
    workspaceId: string,
    input: { runId?: string; top: number; changes?: 'changed' },
  ) => {
    const query = new URLSearchParams({
      top: String(input.top),
      ...(input.runId ? { runId: input.runId } : {}),
      ...(input.changes ? { changes: input.changes } : {}),
    });
    return request<
      | import('./types').FakesGroupedMapPage
      | import('../../../api/src/fakes/serp-map-snapshot.types').SerpMapPending
    >(`/workspaces/${workspaceId}/fakes/grouped-map?${query}`, undefined, (body, response) => {
      const status = response.headers.get('X-Serp-Map-Status');
      return status ? { ...body, snapshotStatus: JSON.parse(decodeURIComponent(status)) } : body;
    });
  },
  fakesMapQueryDetail: (
    workspaceId: string,
    queryId: string,
    input: {
      top: number;
      runId?: string;
      changes?: 'changed';
      groupId: string;
      revision: string;
      builtAt: string;
    },
  ) => {
    const query = new URLSearchParams({
      top: String(input.top),
      groupId: input.groupId,
      revision: input.revision,
      builtAt: input.builtAt,
      ...(input.runId ? { runId: input.runId } : {}),
      ...(input.changes ? { changes: input.changes } : {}),
    });
    return request<import('./types').FakesMatrixRow>(
      `/workspaces/${workspaceId}/fakes/grouped-map/queries/${queryId}?${query}`,
    );
  },
  fakesGroupedMapStatus: (
    workspaceId: string,
    input: { top: number; runId?: string; changes?: 'changed' },
  ) => {
    const query = new URLSearchParams({
      top: String(input.top),
      ...(input.runId ? { runId: input.runId } : {}),
      ...(input.changes ? { changes: input.changes } : {}),
    });
    return request<import('../../../api/src/fakes/serp-map-snapshot.types').SerpMapSnapshotStatus>(
      `/workspaces/${workspaceId}/fakes/grouped-map/status?${query}`,
    );
  },
  workspaces: () => request<Workspace[]>('/workspaces'),
  createWorkspace: (payload: { name: string; siteUrl: string }) =>
    request<Workspace>('/workspaces', json('POST', payload)),
  metrics: (workspaceId: string) => request<MetricSnapshot[]>(`/workspaces/${workspaceId}/metrics`),
  createMetric: (
    workspaceId: string,
    payload: Omit<MetricSnapshot, 'id' | 'workspaceId' | 'createdAt'>,
  ) => request<MetricSnapshot>(`/workspaces/${workspaceId}/metrics`, json('POST', payload)),
  rankDashboard: (workspaceId: string) =>
    request<RankDashboard>(`/workspaces/${workspaceId}/rank/dashboard`),
  rankProject: (workspaceId: string, projectId: string) =>
    request<RankProjectDetail>(`/workspaces/${workspaceId}/rank/projects/${projectId}`),
  createRankProject: (
    workspaceId: string,
    payload: {
      name: string;
      domain: string;
      regionCode?: string;
      regionName?: string;
      device?: 'desktop' | 'mobile';
      depth?: number;
      keywords?: string[];
    },
  ) =>
    request<RankProjectDetail>(`/workspaces/${workspaceId}/rank/projects`, json('POST', payload)),
  updateRankProject: (
    workspaceId: string,
    projectId: string,
    payload: Partial<{
      name: string;
      domain: string;
      regionCode: string;
      regionName: string;
      device: 'desktop' | 'mobile';
      depth: number;
    }>,
  ) =>
    request<RankProjectDetail>(
      `/workspaces/${workspaceId}/rank/projects/${projectId}`,
      json('PATCH', payload),
    ),
  archiveRankProject: (workspaceId: string, projectId: string) =>
    request<{ archived: true; id: string }>(
      `/workspaces/${workspaceId}/rank/projects/${projectId}`,
      { method: 'DELETE' },
    ),
  refreshRankTargetCheck: (workspaceId: string, projectId: string) =>
    request<RankTargetCheck>(
      `/workspaces/${workspaceId}/rank/projects/${projectId}/target-checks`,
      { method: 'POST' },
    ),
  addRankKeywords: (workspaceId: string, projectId: string, queries: string[], source = 'manual') =>
    request<{ added: number; restored: number; totalReceived: number }>(
      `/workspaces/${workspaceId}/rank/projects/${projectId}/keywords`,
      json('POST', { queries, source }),
    ),
  removeRankKeyword: (workspaceId: string, projectId: string, keywordId: string) =>
    request<{ archived: true; id: string }>(
      `/workspaces/${workspaceId}/rank/projects/${projectId}/keywords/${keywordId}`,
      { method: 'DELETE' },
    ),
  rankRuns: (workspaceId: string) => request<RankRun[]>(`/workspaces/${workspaceId}/rank/runs`),
  startRankRun: (workspaceId: string) =>
    request<RankRun>(`/workspaces/${workspaceId}/rank/runs`, { method: 'POST' }),
  startInstantRankRun: (workspaceId: string, projectId: string) =>
    request<RankRun>(`/workspaces/${workspaceId}/rank/projects/${projectId}/instant-runs`, {
      method: 'POST',
    }),
  fakesKeywords: (workspaceId: string) =>
    request<FakesKeywordList>(`/workspaces/${workspaceId}/fakes/keywords`),
  fakesReportSettings: (w: string, groupId = w) =>
    request<FakesReportSettingsView>(
      `/workspaces/${w}/fakes/reports/settings?groupId=${encodeURIComponent(groupId)}`,
    ),
  saveFakesReportSettings: (w: string, input: FakesReportSettingsInput, groupId = w) =>
    request<FakesReportSettingsView>(
      `/workspaces/${w}/fakes/reports/settings?groupId=${encodeURIComponent(groupId)}`,
      json('PUT', input),
    ),
  fakesReportPreview: (w: string, groupId = w) =>
    request<{ html: string; caption: string; imageBase64: string; filename: string }>(
      `/workspaces/${w}/fakes/reports/preview?groupId=${encodeURIComponent(groupId)}`,
    ),
  fakesReportDocument: (w: string, id: string) =>
    request<{ html: string; caption: string; filename: string; imageBase64: string | null }>(
      `/workspaces/${w}/fakes/reports/${id}/document`,
    ),
  sendTodayFakesReport: (w: string, revision: number, groupId = w) =>
    request<FakesReportSettingsView>(
      `/workspaces/${w}/fakes/reports/send-today?groupId=${encodeURIComponent(groupId)}`,
      json('POST', { revision }),
    ),
  fakesReports: (w: string, page = 1, groupId = w) =>
    request<{ items: FakesReportView[]; total: number; page: number; pageSize: number }>(
      `/workspaces/${w}/fakes/reports?page=${page}&pageSize=10&groupId=${encodeURIComponent(groupId)}`,
    ),
  fakesSettings: (workspaceId: string, groupId = workspaceId) =>
    request<FakesMatrixSettings>(
      `/workspaces/${workspaceId}/fakes/settings?groupId=${encodeURIComponent(groupId)}`,
    ),
  jobs: (
    workspaceId: string,
    filters: import('../../../api/src/jobs/jobs.types').JobsQuery,
    system = false,
  ) => {
    const query = new URLSearchParams(
      Object.entries(filters)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, String(v)]),
    );
    return request<import('../../../api/src/jobs/jobs.types').JobsPage>(
      `${system ? '/admin' : '/workspaces/' + workspaceId}/jobs?${query}`,
    );
  },
  jobResourceProfiles: () =>
    request<import('../../../api/src/queue/task-resource-profile').TaskResourceProfilesView>(
      '/admin/jobs/resources',
    ),
  job: (workspaceId: string, key: string, page = 1, system = false) =>
    request<import('../../../api/src/jobs/jobs.types').JobDetail>(
      `${system ? '/admin' : '/workspaces/' + workspaceId}/jobs/${encodeURIComponent(key)}?page=${page}&pageSize=25`,
    ),
  saveFakesGroup: (
    workspaceId: string,
    payload: { name: string; revision: number },
    groupId?: string,
  ) =>
    request<{ group: { id: string; name: string }; state: FakesMatrixSettings }>(
      `/workspaces/${workspaceId}/fakes/groups${groupId ? `/${encodeURIComponent(groupId)}` : ''}`,
      json(groupId ? 'PATCH' : 'POST', payload),
    ),
  updateFakesProfile: (
    workspaceId: string,
    payload: Omit<FakesSearchProfile, 'id'> & {
      groupId?: string;
      revision: number;
      yandexSyncFallbackOn429?: boolean;
    },
  ) =>
    request<FakesMatrixSettings>(`/workspaces/${workspaceId}/fakes/profile`, json('PUT', payload)),
  importFakesQueries: (
    workspaceId: string,
    payload: { queries: string[]; revision: number; groupId?: string },
  ) =>
    request<{ added: number; skipped: number; state: FakesMatrixSettings }>(
      `/workspaces/${workspaceId}/fakes/queries/import`,
      json('POST', payload),
    ),
  updateFakesQuery: (
    workspaceId: string,
    queryId: string,
    payload: { active?: boolean; priority?: boolean; revision: number; groupId?: string },
  ) =>
    request<FakesMatrixSettings>(
      `/workspaces/${workspaceId}/fakes/queries/${queryId}`,
      json('PATCH', payload),
    ),
  startFakesRun: (
    workspaceId: string,
    payload: { requestId: string; profileId: string; revision: number; groupId?: string },
  ) => request<FakesRunSummary>(`/workspaces/${workspaceId}/fakes/runs`, json('POST', payload)),
  fakesRuns: (workspaceId: string, page = 1, groupId = workspaceId) =>
    request<FakesRunPage>(
      `/workspaces/${workspaceId}/fakes/runs?page=${page}&pageSize=10&groupId=${encodeURIComponent(groupId)}`,
    ),
  fakesVolumes: (workspaceId: string) =>
    request<import('../../../api/src/fakes/query-volume').VolumeState>(
      `/workspaces/${workspaceId}/fakes/volumes`,
    ),
  fakesVolumeHistory: (workspaceId: string, before?: string) =>
    request<import('../../../api/src/fakes/volume-batch').VolumeBatchHistory>(
      `/workspaces/${workspaceId}/fakes/volumes/batches${before ? `?before=${encodeURIComponent(before)}` : ''}`,
    ),
  fakesVolumeDetails: (
    workspaceId: string,
    batchId: string,
    page: number,
    status?: import('../../../api/src/fakes/query-volume').QueryVolumeRefresh['status'],
  ) =>
    request<import('../../../api/src/fakes/volume-batch').VolumeBatchDetails>(
      `/workspaces/${workspaceId}/fakes/volumes/batches/${batchId}?page=${page}&pageSize=25${status ? `&status=${status}` : ''}`,
    ),
  stopFakesVolumes: (workspaceId: string, batchId: string) =>
    request<import('../../../api/src/fakes/volume-batch').VolumeBatchHistoryItem>(
      `/workspaces/${workspaceId}/fakes/volumes/batches/${batchId}/stop`,
      { method: 'POST' },
    ),
  planFakesVolumes: (
    workspaceId: string,
    payload: {
      profileId?: string;
      context?: import('../../../api/src/fakes/volume-context').VolumeContext;
      revision: number;
      googleGeoTargetId?: string;
      googleLanguageId?: 'all' | '1031' | '1000';
    },
  ) =>
    request<import('../../../api/src/fakes/volume-plan').VolumePlan>(
      `/workspaces/${workspaceId}/fakes/volumes/plan`,
      json('POST', payload),
    ),
  collectFakesVolumes: (
    workspaceId: string,
    payload: {
      requestId: string;
      profileId?: string;
      context?: import('../../../api/src/fakes/volume-context').VolumeContext;
      revision: number;
      googleGeoTargetId?: string;
      googleLanguageId?: 'all' | '1031' | '1000';
      retryFailed?: boolean;
      onlyMissing?: boolean;
      plannedRequests?: number;
    },
  ) =>
    request<import('../../../api/src/fakes/query-volume').VolumeBatch>(
      `/workspaces/${workspaceId}/fakes/volumes`,
      json('POST', payload),
    ),
  saveFakesVolumeSchedule: (
    workspaceId: string,
    payload: import('../../../api/src/fakes/fakes-volume-schedule').VolumeScheduleInput,
  ) =>
    request<import('../../../api/src/fakes/fakes-volume-schedule').VolumeSchedule>(
      `/workspaces/${workspaceId}/fakes/volumes/schedule`,
      json('PUT', payload),
    ),
  fakesVolumeRegions: (workspaceId: string, query: string) =>
    request<Array<{ id: string; name: string; countryCode: string; type: string }>>(
      `/workspaces/${workspaceId}/fakes/volumes/google-regions`,
      json('POST', { query }),
    ),
  fakesMatrix: (
    workspaceId: string,
    options: {
      groupId?: string;
      runId?: string;
      page: number;
      pageSize: number;
      top: number;
      changes?: 'changed';
    },
  ) => {
    const query = new URLSearchParams({
      page: String(options.page),
      pageSize: String(options.pageSize),
      top: String(options.top),
    });
    if (options.groupId) query.set('groupId', options.groupId);
    if (options.runId) query.set('runId', options.runId);
    if (options.changes) query.set('changes', options.changes);
    return request<FakesMatrixPage>(`/workspaces/${workspaceId}/fakes/matrix?${query}`);
  },
  fakesProgress: (workspaceId: string, options: { groupId: string; runId?: string }) => {
    const query = new URLSearchParams({ groupId: options.groupId });
    if (options.runId) query.set('runId', options.runId);
    return request<import('../../../api/src/fakes/fakes-matrix.types').FakesProgress>(
      `/workspaces/${workspaceId}/fakes/progress?${query}`,
    );
  },
  createFakesKeyword: (workspaceId: string, payload: SearchKeywordInput) =>
    request<SearchKeywordMutation>(
      `/workspaces/${workspaceId}/fakes/keywords`,
      json('POST', payload),
    ),
  refreshFakesKeyword: (workspaceId: string, keywordId: string) =>
    request<SearchKeywordMutation>(
      `/workspaces/${workspaceId}/fakes/keywords/${keywordId}/refresh`,
      { method: 'POST' },
    ),
  fakesSnapshots: (workspaceId: string, keywordId: string, page = 1) =>
    request<SearchSnapshotPage>(
      `/workspaces/${workspaceId}/fakes/keywords/${keywordId}/snapshots?page=${page}&pageSize=10`,
    ),
  fakesResults: (
    workspaceId: string,
    keywordId: string,
    options: {
      page: number;
      pageSize: number;
      snapshotId?: string;
      classification?: FakeClassification;
    },
  ) => {
    const query = new URLSearchParams({
      page: String(options.page),
      pageSize: String(options.pageSize),
    });
    if (options.snapshotId) query.set('snapshotId', options.snapshotId);
    if (options.classification) query.set('classification', options.classification);
    return request<FakesResultPage>(
      `/workspaces/${workspaceId}/fakes/keywords/${keywordId}/results?${query}`,
    );
  },
  n8nDomainsSettings: () => request<N8nDomainsSettingsView>('/admin/n8n-domains'),
  saveN8nDomainsSettings: (input: DailyScheduleSettings) =>
    request<N8nDomainsSettingsView>('/admin/n8n-domains', json('PUT', input)),
  syncN8nDomains: () =>
    request<N8nDomainsSettingsView>('/admin/n8n-domains/sync', { method: 'POST' }),
  fakesCollectionSettings: (w: string, groupId = w) =>
    request<FakesCollectionSettingsView>(
      `/workspaces/${w}/fakes/collection-settings?groupId=${encodeURIComponent(groupId)}`,
    ),
  saveFakesCollectionSettings: (w: string, input: DailyScheduleSettings, groupId = w) =>
    request<FakesCollectionSettingsView>(
      `/workspaces/${w}/fakes/collection-settings?groupId=${encodeURIComponent(groupId)}`,
      json('PUT', input),
    ),
  syncFakesRegistry: (workspaceId: string) =>
    request<FakeRegistryStatus>(`/workspaces/${workspaceId}/fakes/registry/sync`, {
      method: 'POST',
    }),
  serpAiPlan: (w: string, scope: import('../../../api/src/fakes/serp-ai.types').AiSelectionScope) =>
    request<import('../../../api/src/fakes/serp-ai.types').AiSelectionPlan>(
      `/workspaces/${w}/fakes/ai/selection-plan`,
      json('POST', scope),
    ),
  serpAiBatches: (w: string) =>
    request<import('../../../api/src/fakes/serp-ai.types').AiBatch[]>(
      `/workspaces/${w}/fakes/ai/batches`,
    ),
  startSerpAiBatch: (
    w: string,
    input: import('../../../api/src/fakes/serp-ai.types').AiSelectionScope & {
      fingerprint: string;
      profileRevision: number;
    },
  ) =>
    request<import('../../../api/src/fakes/serp-ai.types').AiBatch>(
      `/workspaces/${w}/fakes/ai/batches`,
      json('POST', input),
    ),
  stopSerpAiBatch: (w: string, id: string) =>
    request<import('../../../api/src/fakes/serp-ai.types').AiBatch>(
      `/workspaces/${w}/fakes/ai/batches/${id}/stop`,
      json('POST', {}),
    ),
  acceptSerpAi: (w: string, ids: string[]) =>
    request<import('../../../api/src/fakes/serp-ai.types').AiBulkReview>(
      `/workspaces/${w}/fakes/ai/proposals/accept`,
      json('POST', { ids }),
    ),
  serpAiSettings: (w: string) =>
    request<import('../../../api/src/fakes/serp-ai.types').AiSettings>(
      `/workspaces/${w}/fakes/ai/settings`,
    ),
  saveSerpAiProfile: (w: string, input: { brief: string; expectedRevision: number }) =>
    request<import('../../../api/src/fakes/serp-ai.types').AiSettings>(
      `/workspaces/${w}/fakes/ai/settings`,
      json('PUT', input),
    ),
  serpAiProposals: (
    w: string,
    input: {
      resultId?: string;
      page?: number;
      batchId?: string;
      pendingOnly?: boolean;
      category?: import('../../../api/src/fakes/serp-ai.types').AiCategory;
    } = {},
  ) =>
    request<import('../../../api/src/fakes/serp-ai.types').AiProposalPage>(
      `/workspaces/${w}/fakes/ai/proposals?${new URLSearchParams({ page: String(input.page ?? 1), ...(input.resultId ? { resultId: input.resultId } : {}), ...(input.batchId ? { batchId: input.batchId } : {}), ...(input.pendingOnly ? { pendingOnly: 'yes' } : {}), ...(input.category ? { category: input.category } : {}) })}`,
    ),
  startSerpAi: (
    w: string,
    input: { resultIds: string[]; profileRevision: number; retryId?: string },
  ) =>
    request<{ ids: string[]; skipped: Array<{ resultId: string; reason: string }> }>(
      `/workspaces/${w}/fakes/ai/proposals`,
      json('POST', input),
    ),
  reviewSerpAi: (
    w: string,
    id: string,
    input: {
      action: 'accept' | 'correct' | 'reject';
      category?: import('../../../api/src/fakes/serp-ai.types').AiCategory;
      sentiment?: import('../../../api/src/fakes/serp-classification').MentionSentiment | null;
      reason?: string;
    },
  ) =>
    request<{ review: import('../../../api/src/fakes/serp-ai.types').AiProposal['review'] }>(
      `/workspaces/${w}/fakes/ai/proposals/${id}/review`,
      json('POST', input),
    ),
  serpPageCaptures: (w: string, id: string) =>
    request<import('../../../api/src/fakes/serp-page.types').PageCapturesView>(
      `/workspaces/${w}/fakes/results/${id}/page-captures`,
    ),
  captureSerpPage: (w: string, id: string, input: { refresh?: boolean; expectedId?: string }) =>
    request<import('../../../api/src/fakes/serp-page.types').PageCapture>(
      `/workspaces/${w}/fakes/results/${id}/page-captures`,
      json('POST', input),
    ),
  decideFake: (
    workspaceId: string,
    resultId: string,
    payload: {
      scope: 'url' | 'host' | 'channel';
      verdict: FakeVerdict;
      reason: string;
      expectedVersion: string;
      mentionSentiment?: import('../../../api/src/fakes/fakes.types').MentionSentiment | null;
    },
  ) =>
    request<FakeDecision>(
      `/workspaces/${workspaceId}/fakes/results/${resultId}/decisions`,
      json('POST', payload),
    ),
  decideRelevance: (
    w: string,
    id: string,
    input: { noise: boolean; reason: string; expectedId: string | null },
  ) =>
    request<RelevanceDecision>(
      `/workspaces/${w}/fakes/results/${id}/relevance`,
      json('POST', input),
    ),
  relevanceHistory: (w: string, id: string) =>
    request<RelevanceDecision[]>(`/workspaces/${w}/fakes/results/${id}/relevance`),
  fakeDecisions: (workspaceId: string, resultId: string) =>
    request<FakeDecision[]>(`/workspaces/${workspaceId}/fakes/results/${resultId}/decisions`),
  searchYandexRegions: (workspaceId: string, query = '') =>
    request<{ items: SearchRegion[] }>(
      `/workspaces/${workspaceId}/search/regions/yandex?query=${encodeURIComponent(query)}`,
    ),
  publishing: (w: string, page = 1, pageSize: PublishingPageSize = PUBLISHING_PAGE_SIZES[0]) =>
    request<PublishingOverview>(
      `/workspaces/${w}/telegram/publishing?page=${page}&pageSize=${pageSize}`,
    ),
  publishingMessageSettings: (w: string, input: PublishingMessageSettings) =>
    request<PublishingMessageSettings>(
      `/workspaces/${w}/telegram/publishing/message-settings`,
      json('PATCH', input),
    ),
  publishingIntegration: (w: string, sourceUrl: string, revision: number) =>
    request<PublishingIntegrationView>(
      `/workspaces/${w}/telegram/publishing/integration`,
      json('POST', { sourceUrl, revision }),
    ),
  publishingCities: (w: string, refresh = false) =>
    request<PublishingCities>(
      `/workspaces/${w}/telegram/publishing/cities${refresh ? '/refresh' : ''}`,
      refresh ? json('POST', {}) : undefined,
    ),
  publishingBot: (w: string, token: string) =>
    request<PublishingBot>(`/workspaces/${w}/telegram/publishing/bots`, json('POST', { token })),
  publishingFilterDirectory: (w: string, cityId: number, sourceRevision: number, tags: string[]) =>
    request<PublishingFilterDirectory>(
      `/workspaces/${w}/telegram/publishing/filters/directory`,
      json('POST', { cityId, sourceRevision, tags }),
    ),
  publishingFilterPreview: (
    w: string,
    cityId: number,
    sourceRevision: number,
    filters: PublishingFilters,
  ) =>
    request<PublishingFilterPreview>(
      `/workspaces/${w}/telegram/publishing/filters/preview`,
      json('POST', { cityId, sourceRevision, filters }),
    ),
  publishingCheckBotPermissions: (w: string, botId: string, chatId: string) =>
    request<PublishingBotPermissions>(
      `/workspaces/${w}/telegram/publishing/channels/check-permissions`,
      json('POST', { botId, chatId }),
    ),
  publishingSave: (w: string, input: PublishingSettings & { revision?: number }, id?: string) =>
    request<PublishingChannelView>(
      `/workspaces/${w}/telegram/publishing/channels${id ? `/${id}` : ''}`,
      json(id ? 'PATCH' : 'POST', input),
    ),
  publishingEnabled: (w: string, id: string, revision: number, enabled: boolean) =>
    request<PublishingChannelView>(
      `/workspaces/${w}/telegram/publishing/channels/${id}/enabled`,
      json('PATCH', { revision, enabled }),
    ),
  publishingRefresh: (w: string, id: string) =>
    request<{ queued: boolean }>(
      `/workspaces/${w}/telegram/publishing/channels/${id}/refresh`,
      json('POST', {}),
    ),
  publishingPreview: (w: string, id: string) =>
    request<PublishingPreview>(`/workspaces/${w}/telegram/publishing/channels/${id}/preview`),
  publishingHistory: (w: string, id: string, page = 1) =>
    request<PublishingHistory>(
      `/workspaces/${w}/telegram/publishing/channels/${id}/history?page=${page}`,
    ),
  publishingSkipped: (w: string, id: string) =>
    request<Array<{ personId: number; reason: string }>>(
      `/workspaces/${w}/telegram/publishing/channels/${id}/skipped`,
    ),
  publishingResolve: (
    w: string,
    id: string,
    deliveryId: string,
    outcome: 'sent' | 'not_sent',
    messageIds?: number[],
  ) =>
    request<{ resolved: boolean }>(
      `/workspaces/${w}/telegram/publishing/channels/${id}/history/${deliveryId}/resolve`,
      json('POST', { outcome, messageIds }),
    ),
  telegramRegistry: (w: string) =>
    request<TelegramOwnedChannel[]>(`/workspaces/${w}/telegram/registry`),
  createTelegramRegistryEntry: (w: string, input: TelegramRegistryEntry) =>
    request<TelegramOwnedChannel>(`/workspaces/${w}/telegram/registry`, json('POST', input)),
  updateTelegramRegistryEntry: (
    w: string,
    id: string,
    input: { title: string; notes: string; owned: boolean; revision: number },
  ) =>
    request<TelegramOwnedChannel>(`/workspaces/${w}/telegram/registry/${id}`, json('PATCH', input)),
  telegramRegistryHistory: (w: string, id: string) =>
    request<TelegramOwnedChannel[]>(`/workspaces/${w}/telegram/registry/${id}/history`),
  telegram: (workspaceId: string) =>
    request<TelegramChannel[]>(`/workspaces/${workspaceId}/telegram`),
  saveTelegram: (
    workspaceId: string,
    payload: { id?: string; title: string; chatId: string; botToken?: string; enabled?: boolean },
  ) => request<TelegramChannel>(`/workspaces/${workspaceId}/telegram`, json('POST', payload)),
  topvisorReports: (workspaceId: string) =>
    request<TopvisorReport[]>(`/workspaces/${workspaceId}/reports`),
  topvisorReport: (workspaceId: string, reportId: string) =>
    request<TopvisorReport>(`/workspaces/${workspaceId}/reports/${reportId}`),
  topvisorCatalog: (workspaceId: string) =>
    request<TopvisorCatalog>(`/workspaces/${workspaceId}/reports/catalog`),
  importTopvisorReports: (
    workspaceId: string,
    items: Array<{
      projectId: number;
      regionIndex: number;
      positions: boolean;
      serp: boolean;
    }>,
  ) =>
    request<TopvisorCatalogImportResult>(
      `/workspaces/${workspaceId}/reports/import`,
      json('POST', { items }),
    ),
  createTopvisorReport: (
    workspaceId: string,
    payload: {
      title: string;
      type: 'positions' | 'serp';
      topvisorProjectId: number;
      regionIndex: number;
      searchEngineLabel?: string;
      regionLabel: string;
      device?: 'desktop' | 'tablet' | 'mobile';
      depth?: number;
      comparisonProjectIds?: number[];
      trackedHosts?: string[];
    },
  ) => request<TopvisorReport>(`/workspaces/${workspaceId}/reports`, json('POST', payload)),
  syncTopvisorReport: (workspaceId: string, reportId: string) =>
    request<TopvisorReport>(`/workspaces/${workspaceId}/reports/${reportId}/sync`, {
      method: 'POST',
    }),
  archiveTopvisorReport: (workspaceId: string, reportId: string) =>
    request<{ archived: true; id: string }>(`/workspaces/${workspaceId}/reports/${reportId}`, {
      method: 'DELETE',
    }),
  shorts: (workspaceId: string) => request<ShortsDraft[]>(`/workspaces/${workspaceId}/shorts`),
  createShort: (
    workspaceId: string,
    payload: Pick<ShortsDraft, 'title'> &
      Partial<Omit<ShortsDraft, 'id' | 'workspaceId' | 'title'>>,
  ) => request<ShortsDraft>(`/workspaces/${workspaceId}/shorts`, json('POST', payload)),
  updateShort: (workspaceId: string, draftId: string, payload: Partial<ShortsDraft>) =>
    request<ShortsDraft>(`/workspaces/${workspaceId}/shorts/${draftId}`, json('PATCH', payload)),
};
